import { APIError } from '../api/apiRequest';
import * as noticesStore from '../stores/noticesStore';
import {
  openActivateEarlyTrialModal,
  openPurchaseCreditsModal,
} from '../stores/updatedProductStore';
import type { OrganizationInfo } from '../types';
import {
  getOrgAvailableCredits,
  isOrgCreditsWorkspace,
} from './organizationWorkspace';

function isCreditOrPaymentBlockMessage(message: string): boolean {
  const m = message.toLowerCase();
  if (m.includes('insufficient')) {
    return true;
  }
  if (m.includes('not enough') && m.includes('credit')) {
    return true;
  }
  if (m.includes('balance in your account')) {
    return true;
  }
  if (m.includes('organization credit')) {
    return true;
  }
  return false;
}

/**
 * Handles 402 “can’t pay / out of credits” responses for org-credit workspaces:
 * - Shows a notices error so the user sees what went wrong.
 * - **Trialing** (`inStripeFreeTrialPeriod`): opens **Activate early trial** only
 *   (not purchase credits — see `PurchaseCreditsModalHost`).
 * - **Not trialing**: opens **Purchase credits** (not activate early).
 *
 * @returns true if this was a handled org-credit 402 (caller should skip generic error UI).
 */
export function handleOrgCreditBlockApiError(
  error: APIError | undefined,
  organizationInfo: OrganizationInfo | null | undefined,
): boolean {
  if (!error) {
    return false;
  }
  if (!(error instanceof APIError) || error.status !== 402) {
    return false;
  }
  if (!organizationInfo || !isOrgCreditsWorkspace(organizationInfo)) {
    return false;
  }
  if (!isCreditOrPaymentBlockMessage(error.message)) {
    return false;
  }

  const detail = error.message?.trim() ?? '';
  noticesStore.error(
    detail
      ? `You’re out of organization credits. ${detail}`
      : 'You’re out of organization credits.',
  );

  if (organizationInfo.inStripeFreeTrialPeriod === true) {
    openActivateEarlyTrialModal();
  } else {
    const remaining = getOrgAvailableCredits(organizationInfo);
    openPurchaseCreditsModal({
      variant: remaining <= 0 ? 'out_of_credits' : 'low_credit',
      creditsRemaining: remaining,
    });
  }
  return true;
}
